import { sortBy } from 'lodash'
import { formatCentsToCurrency } from 'utils/price'
import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'

const generateMaterialSummary = data => {
  if (!data) return ''

  const quantity = data.quantity || 0
  const unitOfMeasure = data.unitOfMeasure || ''
  const unitPriceCents = data.unitPriceCents || 0
  const rate = data.rate || 0
  const useSiteRate = data.useSiteRate

  const price = formatCentsToCurrency(unitPriceCents)
  const total = formatCentsToCurrency(quantity * unitPriceCents)

  let summary = `(${quantity} ${unitOfMeasure}) x ${price} = ${total}`

  if (useSiteRate) {
    const siteRate = formatCentsToCurrency(rate)
    summary += ` (Site Rate: ${siteRate})`
  }

  return summary
}

export default function disposalTicketBuilder(builder, payload = {}) {
  const { disposalTickets } = payload || {}

  if (!disposalTickets) return

  Object.values(disposalTickets).forEach(versions => {
    if (!Array.isArray(versions) || versions.length === 0) return

    // 1. Pre-process versions to add derived fields for Ticket
    const processedVersions = versions.map(v => {
      const { onlyDate } = convertDateTimeToPreferedTimezone({ dateTime: v.effectiveDate })
      return {
        ...v,
        status: v.deleted ? 'Deleted' : 'Active',
        formattedEffectiveDate: onlyDate,
        taxableStatus: v.taxable ? 'Yes' : 'No',
      }
    })

    builder.addChanges(processedVersions, {
      entityName: 'Disposal Ticket',
      fieldConfig: {
        ticketNumber: 'Ticket Number',
        formattedEffectiveDate: 'Effective Date',
        taxableStatus: 'Taxable',
        status: 'Status',
      },
    })

    // 2. Track Material Changes
    const sorted = sortBy(versions, v => Number(v?.rev ?? 0))
    const materialMap = new Map()

    for (let i = 0; i < sorted.length; i++) {
      const currentTicket = sorted[i]
      const rev = currentTicket.rev

      const currentMaterials = currentTicket.materials || []

      currentMaterials.forEach(curr => {
        const prev = materialMap.get(curr.id)

        let action = null
        let oldValue = null
        let newValue = null

        if (!prev) {
          if (!curr.deleted) {
            action = 'Added'
            newValue = curr
          }
        } else {
          if (prev.deleted && !curr.deleted) {
            action = 'Restored'
            oldValue = prev
            newValue = curr
          } else if (!prev.deleted && curr.deleted) {
            action = 'Deleted'
            oldValue = prev
            newValue = null
          } else if (!prev.deleted && !curr.deleted) {
            if (curr.rev !== prev.rev) {
              action = 'Updated'
              oldValue = prev
              newValue = curr
            }
          }
        }

        if (action) {
          if (newValue) {
            newValue = { ...newValue }
            newValue.toString = () => generateMaterialSummary(newValue)
          }
          if (oldValue) {
            oldValue = { ...oldValue }
            oldValue.toString = () => generateMaterialSummary(oldValue)
          }

          builder.addCustomChange(rev, currentTicket, {
            field: 'Disposal Ticket - Material',
            fieldKey: 'disposalTicketMaterial',
            oldValue,
            newValue,
            changeType: action,
            renderRawValue: true,
          })
        }

        materialMap.set(curr.id, curr)
      })
    }
  })
}
